import { get, set, includes, each } from 'lodash'
import $ from 'jquery'
import FPDManager from "./fpd-manager"


/**
 *  The dynamic views operator is listing to changes in a form and updating the view accordingly.
 *
 *  Todos:
 *  - fix hidden form fields with custom width and height
 *  - fix support for cm and inch (see ViewsNav.js)
 */
export default class DynamicViewsOperator {
  constructor(productPage) {
    this.productPage = productPage
    this.viewNavWidth = null
    this.viewNavHeight = null
    this.initCount = 0
    this.maxInitCount = 10
  }

  get optionsManager() {
    return this.productPage.optionsManager
  }

  get taDyanmicViews() {
    return this.optionsManager.ta.dynamicViews || {}
  }

  get taViewNavInputsWidth() {
    return this.taDyanmicViews.viewNavInputsWidth || ".fpd-view-edit-size input[data-type='width']"
  }

  get taViewNavInputsHeight() {
    return this.taDyanmicViews.viewNavInputsHeight || ".fpd-view-edit-size input[data-type='height']"
  }

  get taExternalInputsWidth() {
    return this.taDyanmicViews.externalInputsWidth || ".fpd-view-size [name='width']"
  }

  get taExternalInputsHeight() {
    return this.taDyanmicViews.externalInputsHeight || ".fpd-view-size [name='height']"
  }

  isInputStyleSelect() {
    return this.dynamicViewsOptions.inputStyle === 'select'
  }

  initListener() {
    if (!this.productPage.isDynamicViewsEnabled()) {
      return
    }

    this.initViewNavInputs()
    if (this.viewNavWidth && this.viewNavWidth.value) {
      this.initDynamicViewsOptions()
      if (this.isInputStyleSelect()) {
        this.initExternalSelect()
      } else {
        this.initExternalInputs()
      }
      this.initHiddenFormFields()
      this.initChangeListeners()
      this.initViewSelectionListener()
      this.initInputUpdateListener()
    } else {
      this.initCount++
      if (this.initCount < this.maxInitCount) {
        setTimeout(this.initListener.bind(this), 1000)
      }
    }
  }

  initDynamicViewsOptions() {
    this.dynamicViewsOptions = FPDManager.instance.mainOptions.dynamicViewsOptions
  }

  initViewNavInputs() {
    this.viewNavWidth = document.querySelector(this.taViewNavInputsWidth)
    this.viewNavHeight = document.querySelector(this.taViewNavInputsHeight)
  }

  initExternalInputs() {
    this.externalWidth = document.querySelector(this.taExternalInputsWidth)
    this.externalWidth.classList += "fpd-tooltip"
    this.externalWidth.min = this.dynamicViewsOptions.minWidth
    this.externalWidth.max = this.dynamicViewsOptions.maxWidth
    this.externalWidth.step = this.unitToStep(this.dynamicViewsOptions.unit)

    const widthConstraints = `${this.dynamicViewsOptions.minWidth}${this.dynamicViewsOptions.unit} - ${this.dynamicViewsOptions.maxWidth}${this.dynamicViewsOptions.unit}`
    this.externalWidth.setAttribute('aria-label', widthConstraints)
    this.externalWidth.setAttribute('placeholder', widthConstraints)

    this.externalHeight = document.querySelector(this.taExternalInputsHeight)
    this.externalHeight.classList += "fpd-tooltip"
    this.externalHeight.min = this.dynamicViewsOptions.minHeight
    this.externalHeight.max = this.dynamicViewsOptions.maxHeight
    this.externalHeight.step = this.unitToStep(this.dynamicViewsOptions.unit)

    const heightConstraints = `${this.dynamicViewsOptions.minHeight}${this.dynamicViewsOptions.unit} - ${this.dynamicViewsOptions.maxHeight}${this.dynamicViewsOptions.unit}`
    this.externalHeight.setAttribute('aria-label', heightConstraints)
    this.externalHeight.setAttribute('placeholder', heightConstraints)
  }

  initExternalSelect() {
    this.externalWidth = document.querySelector(this.taExternalInputsWidth)
    this.externalWidth.classList += "fpd-tooltip"
    const { unit, minWidth, maxWidth } = this.dynamicViewsOptions
    this.externalWidth.innerHTML = ""
    each(this.rangeToOptions(unit, minWidth, maxWidth), (optionData) => {
      const optionElement = document.createElement("option")
      optionElement.value = optionData.value
      optionElement.text = optionData.label
      this.externalWidth.add(optionElement)
    })

    this.externalHeight = document.querySelector(this.taExternalInputsHeight)
    this.externalHeight.classList += "fpd-tooltip"
    const { minHeight, maxHeight } = this.dynamicViewsOptions
    this.externalHeight.innerHTML = ""
    each(this.rangeToOptions(unit, minHeight, maxHeight), (optionData) => {
      const optionElement = document.createElement("option")
      optionElement.value = optionData.value
      optionElement.text = optionData.label
      this.externalHeight.add(optionElement)
    })
  }

  initHiddenFormFields() {
    if (!this.externalHeight || !this.externalWidth) {
      FPD.debug && console.debug("DynamicViewsOperator: No external inputs found, skipping initialization.")
      return
    }
    const $form = this.productPage.$addToCartForm
    // todo: this does not work - _custom_width is empty
    if ($form.find('input[name="properties[_custom_width]"]').length > 0) {
      this.hiddenFormFieldWidth = $form.find('input[name="properties[_custom_width]"]')[0]
    } else {
      this.hiddenFormFieldWidth = $(`<input type="hidden" name="properties[_custom_width]" />`)[0]
      $form.append(this.hiddenFormFieldWidth)
    }
    this.hiddenFormFieldWidth.value = this.externalWidth.value

    if ($form.find('input[name="properties[_custom_height]"]').length > 0) {
      this.hiddenFormFieldHeight = $form.find('input[name="properties[_custom_height]"]')[0]
    } else {
      this.hiddenFormFieldHeight = $(`<input type="hidden" name="properties[_custom_height]" />`)[0]
      $form.append(this.hiddenFormFieldHeight)
    }
    this.hiddenFormFieldHeight.value = this.externalHeight.value
  }

  initViewSelectionListener() {
    $(document).on('fpd:view:selected', () => {
      if (this.areInputsElementsPresent() && this.optionsManager.isDynamicViewsUpdateOnViewSelection()) {
        this.externalWidth.value = this.viewNavWidth.value
        this.externalHeight.value = this.viewNavHeight.value
        this.updateHiddenFormFields()
      } else {
        this.updateHiddenFormFields()
        this.updateViewSize()
      }
    })
  }

  initInputUpdateListener() {
    $(document).on('fpd:input:update', (_event) => {
      if (!this.areInputsElementsPresent()) {
        return
      }

      this.updateHiddenFormFields()
      this.updateViewSize()
    })
  }

  initChangeListeners() {
    if (this.areInputsElementsPresent()) {
      this.externalWidth.value = this.viewNavWidth.value
      this.externalWidth.addEventListener('change', this.updateViewSize.bind(this))
      this.externalWidth.addEventListener('change', this.updateHiddenFormFields.bind(this))
      this.externalHeight.value = this.viewNavHeight.value
      this.externalHeight.addEventListener('change', this.updateViewSize.bind(this))
      this.externalHeight.addEventListener('change', this.updateHiddenFormFields.bind(this))

      this.ensureInsideMinMax(this.externalWidth)
      this.ensureInsideMinMax(this.externalHeight)
      this.updateViewSize()
      this.updateHiddenFormFields()
    }
  }

  areInputsElementsPresent() {
    return this.viewNavWidth && this.viewNavHeight && this.externalWidth && this.externalHeight
  }

  updateViewSize() {
    if (this.externalWidth.value) {
      this.ensureInsideMinMax(this.externalWidth)
      this.viewNavWidth.value = this.externalWidth.value
      this.ensureInsideMinMax(this.externalHeight)
      this.viewNavHeight.value = this.externalHeight.value
      this.viewNavWidth.dispatchEvent(new Event('change'))
    }
  }
  updateHiddenFormFields() {
    if (this.externalWidth.value) {
      this.hiddenFormFieldWidth.value = this.externalWidth.value
      this.hiddenFormFieldHeight.value = this.externalHeight.value
    }
  }

  ensureInsideMinMax(input) {
    const value = Number(input.value)
    const min = Number(input.min || 0)
    const max = Number(input.max || 10000)

    if (value < min)
      input.value = min
    if (value > max)
      input.value = max
  }

  rangeToOptions(unit, min, max) {
    let optionValues = []

    min = Number(min)
    max = Number(max)
    if(isNaN(min)) {
      console.error("Range Options: min not a number.", min)
      return optionValues
    }
    if(isNaN(max)) {
      console.error("Range Options: max not a number.", max)
      return optionValues
    }

    if(Math.abs(max - min) > 1000) {
      console.error("Range is big.", Math.abs(max - min))
      return optionValues
    }

    switch (unit) {
      case 'ft':
        for (let i = min; i <= max; i++) {
          for (let j = 0; j <= 11; j++) {
            optionValues.push({ value: i + this.inchToFt(j), label: `${i} ft` + (j != 0 ? ` ${j}"` : "") })
          }
        }
        break
      default:
        for (let i = min; i <= max; i++) {
          optionValues.push({ value: i, label: `${i} ${unit}` })
        }
    }
    return optionValues
  }

  inchToFt(inches) {
    return parseFloat(inches) / 12.0
  }

  unitToStep(unit) {
    switch (unit) {
      case 'mm':
        return "1"
      case 'cm':
        return "0.1"
      case 'inch':
        return "0.1"
      case 'ft':
        return "0.01"
      default:
        return "1"
    }
  }
}